import { useSupabaseLoader } from "../../../server/supabase/hooks";

import type { UUID } from "../../../utils/uuid";

interface RecentProgram {
  id: UUID;
  title: string;
  colour: string | null;
  icon: string | null;
}

const RECENT_PROGRAMS = 10;

// The newest Communications looked through for their programs. Enough to find ten programs in all
// but the most lopsided accounts, without reading every Communication to sort them.
const RECENT_MOMENTS = 200;

/**
 * The programs the account most recently created Communications in, newest first. Communications
 * don't record who created them, so this is the account's, not the user's.
 */
export function useRecentPrograms(): ReturnType<typeof useSupabaseLoader<RecentProgram[]>> {
  return useSupabaseLoader(async ({ supabase, account_id }) => {
    const { data: moments, error: momentsError } = await supabase
      .from("moment")
      .select("program_id")
      .eq("account_id", account_id)
      .not("program_id", "is", null)
      .order("created_at", { ascending: false })
      .limit(RECENT_MOMENTS);
    if (momentsError) throw momentsError;

    const ids = [...new Set(moments.map((moment) => moment.program_id))].slice(0, RECENT_PROGRAMS);
    if (ids.length === 0) return [];

    const { data: programs, error: programsError } = await supabase
      .from("populated_programs")
      .select("id, title, theme_colour, icon_value, visible")
      .in("id", ids);
    if (programsError) throw programsError;

    // Back into the order they were used in, leaving out programs hidden from the list.
    return ids.flatMap((id) => {
      const program = programs.find((candidate) => candidate.id === id);
      return program?.title && program.visible !== false
        ? [{ id, title: program.title, colour: program.theme_colour, icon: program.icon_value }]
        : [];
    });
  }, []);
}
